import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Caption1,
  Card,
  CardHeader,
  Dropdown,
  Option,
  Spinner,
  Toast,
  ToastBody,
  ToastTitle,
  useToastController,
} from "@fluentui/react-components";
import {
  People24Regular,
  PeopleCommunity24Regular,
  Delete24Regular,
  ReceiptMoney24Regular,
  PeopleSync24Regular,
  PictureInPicture24Regular,
  ErrorCircle24Filled,
  PersonStar24Regular,
} from "@fluentui/react-icons";
import Pagination from "../../components/pagination/Pagination";
import TableComponent from "../../components/table/TableComponent";
import { AdminService, KPIFilterType, VoucherType } from "../../KulcsUzletSvc";
import { useStylesMainPage } from "../../styles/MainPageStyle";
import { numberFormatter } from "../../utils/GlobalConsts";
import { TABLE } from "../../utils/Enums";

enum ActiveRangeType {
  LAST_30_DAYS = "LAST_30_DAYS",
  LAST_CALENDAR_MONTH = "LAST_CALENDAR_MONTH",
  DATE_PICKER = "DATE_PICKER",
}

export default function KPIPage() {
  const styles = useStylesMainPage();
  const navigate = useNavigate();
  const [allAccounts, setAllAccounts] = useState(0);
  const [activeAccounts, setActiveAccounts] = useState(0);
  const [deletableAccounts, setDeletableAccounts] = useState(0);
  const [voucherCount, setVoucherCount] = useState(0);
  const { dispatchToast } = useToastController();
  const [loadingAllAccounts, setLoadingAllAccounts] = useState(false);
  const [activeUsersThisMonth, setActiveUsersThisMonth] = useState(0);
  const [currentCoreUsers, setCurrentCoreUsers] = useState<number | null>(0);
  const [canceledCoreUsers, setCanceledCoreUsers] = useState<number | null>(0);
  const [trialUsers, setTrialUsers] = useState<number | null>(0);
  const [freeUsers, setFreeUsers] = useState<number | null>(0);
  const [newRegFlowUsers, setNewRegFlowUsers] = useState<number | null>(null);
  const [migratedBizXUsers, setMigratedBizXUsers] = useState<number | null>(
    null
  );
  const [migratedKeyBusinessUsers, setMigratedKeyBusinessUsers] = useState<
    number | null
  >(null);
  const [registeredUsers, setRegisteredUsers] = useState<number | null>(null);
  const [invoiceCount, setInvoiceCount] = useState<number | null>(null);
  const [invoiceKpiCount, setInvoiceKpiCount] = useState<number | null>(null);
  const [loadingInvoiceKpi, setLoadingInvoiceKpi] = useState(false);
  const [errorInvoiceKpi, setErrorInvoiceKpi] = useState(false);
  const [kpiEvCount, setKpiEvCount] = useState<number | null>(null);
  const [kpiKataCount, setKpiKataCount] = useState<number | null>(null);
  const [kpiEvKataCount, setKpiEvKataCount] = useState<number | null>(null);
  const [loadingKpi, setLoadingKpi] = useState(true);
  const [errorKpi, setErrorKpi] = useState(false);
  type KpiCategory = "KATA" | "EV" | "EVKATA";
  const [loadingActiveAccounts, setLoadingActiveAccounts] = useState(false);
  const [loadingActualState, setLoadingActualState] = useState(false);
  const [loadingDeletable, setLoadingDeletable] = useState(false);
  const [loadingVoucher, setLoadingVoucher] = useState(false);
  const [loadingActiveUsersThisMonth, setLoadingActiveUsersThisMonth] =
    useState(false);
  const [errorAllAccounts, setErrorAllAccounts] = useState(false);
  const [errorActiveAccounts, setErrorActiveAccounts] = useState(false);
  const [errorActualState, setErrorActualState] = useState(false);
  const [errorActiveUsersThisMonth, setErrorActiveUsersThisMonth] =
    useState(false);
  const [errorDeletable, setErrorDeletable] = useState(false);
  const [errorVoucher, setErrorVoucher] = useState(false);
  const [renewalErrors, setRenewalErrors] = useState<any[]>([]);
  const [loadingRenewalErrors, setLoadingRenewalErrors] = useState(false);
  const [errorRenewalErrors, setErrorRenewalErrors] = useState(false);
  const [renewalPageSize, setRenewalPageSize] = useState("25");
  const [renewalCurrentPage, setRenewalCurrentPage] = useState(1);
  const [activeRangeType, setActiveRangeType] = useState<ActiveRangeType>(
    ActiveRangeType.LAST_30_DAYS
  );

  const today = new Date().toISOString().split("T")[0];
  const firstDayOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1
  )
    .toISOString()
    .split("T")[0];

  const [dateFilter, setDateFilter] = useState({
    startDate: firstDayOfMonth,
    endDate: today,
  });

  const [kpiFilterType, setKpiFilterType] = useState<KPIFilterType>(
    KPIFilterType.BOTH
  );
  const [voucherType, setVoucherType] = useState<VoucherType>(
    VoucherType.CUSTOMER_INVOICE
  );

  const navigateBack = () => {
    navigate(-1);
  };

  const getActiveRangeTypeLabel = (type: ActiveRangeType): string => {
    switch (type) {
      case ActiveRangeType.LAST_30_DAYS:
        return "Előző 30 nap";
      case ActiveRangeType.LAST_CALENDAR_MONTH:
        return "Előző naptári hónap";
      case ActiveRangeType.DATE_PICKER:
        return "Dátumválasztó";
      default:
        return "Előző 30 nap";
    }
  };

  const showErrorToast = (title: string, message?: string) => {
    dispatchToast(
      <Toast
        appearance="inverted"
        style={{
          padding: "16px",
          borderRadius: "18px",
          backgroundColor: "rgba(231, 93, 93, 0.77)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.28)",
        }}
      >
        <ToastTitle>{title}</ToastTitle>
        {message && <ToastBody>{message}</ToastBody>}
      </Toast>,
      { intent: "error", toastId: "kpi-toaster" }
    );
  };

  useEffect(() => {
    getActiveUsersThisMonth();
  }, [kpiFilterType]);

  useEffect(() => {
    getAllAccounts();
    getDeletableAccounts();
    getInvoiceKpiCount();
    getRenewalErrors();
  }, [dateFilter, kpiFilterType]);

  const skip = useRef(true);

  useEffect(() => {
    if (skip.current) {
      skip.current = false;
      return;
    }
    getVoucherCount();
  }, [dateFilter, kpiFilterType, voucherType]);

  useEffect(() => {
    getActiveAccounts();
  }, [activeRangeType, kpiFilterType]);

  useEffect(() => {
    if (activeRangeType === ActiveRangeType.DATE_PICKER) {
      getActiveAccounts();
    }
  }, [dateFilter]);

  const getKPICounts = async () => {
    setLoadingKpi(true);
    setErrorKpi(false);

    try {
      const [kataRes, evRes, evKataRes] = await Promise.all([
        AdminService.getKpiCounts("KATA" as KpiCategory, kpiFilterType),
        AdminService.getKpiCounts("EV" as KpiCategory, kpiFilterType),
        AdminService.getKpiCounts("EVKATA" as KpiCategory, kpiFilterType),
      ]);

      if (kataRes) {
        setKpiKataCount(Number(kataRes));
      }

      if (evRes) {
        setKpiEvCount(Number(evRes));
      }

      if (evKataRes) {
        setKpiEvKataCount(Number(evKataRes));
      }
    } catch (error) {
      console.error("Hiba a KPI adatok lekérésekor:", error);
      setErrorKpi(true);
    } finally {
      setLoadingKpi(false);
    }
  };

  useEffect(() => {
    getKPICounts();
  }, [kpiFilterType]);

  const getAllAccounts = async () => {
    setLoadingAllAccounts(true);
    setErrorAllAccounts(false);

    try {
      const res = await AdminService.getAdminKpiAllAccountsCount(
        dateFilter.startDate,
        dateFilter.endDate,
        kpiFilterType
      );
      setAllAccounts(res);
    } catch (e) {
      setErrorAllAccounts(true);
      console.error("getAdminKpiAllAccountsCount failed", e);
      showErrorToast("Hiba történt", "Nem sikerült lekérni az összes fiókot.");
    } finally {
      setLoadingAllAccounts(false);
    }
  };

  const getActiveAccounts = async () => {
    setLoadingActiveAccounts(true);
    setErrorAllAccounts(false);
    let startDate = "";
    let endDate = new Date().toISOString().split("T")[0];

    if (activeRangeType === ActiveRangeType.LAST_30_DAYS) {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      // console.log(d, d.getDate());
      startDate = d.toISOString().split("T")[0];
    } else if (activeRangeType === ActiveRangeType.LAST_CALENDAR_MONTH) {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      startDate = first.toISOString().split("T")[0];
      endDate = last.toISOString().split("T")[0];
    } else if (activeRangeType === ActiveRangeType.DATE_PICKER) {
      startDate = dateFilter.startDate;
      endDate = dateFilter.endDate;
    }

    try {
      const res = await AdminService.getAdminKpiActiveAccountsCount(
        startDate,
        endDate,
        kpiFilterType
      );

      setActiveAccounts(res);
    } catch (e) {
      setErrorActiveAccounts(true);
      console.error("getAdminKpiActiveAccounts failed", e);
      showErrorToast("Hiba történt", "Nem sikerült lekérni az aktív fiókot.");
    } finally {
      setLoadingActiveAccounts(false);
    }
  };

  useEffect(() => {
    getActualState();
  }, []);

  const getActualState = async () => {
    setLoadingActualState(true);
    setErrorActualState(false);

    try {
      const res =
        (await AdminService.getCurrentSubscriptionCounts()) as unknown as {
          activeCount: number;
          cancelledCount: number;
          trialCount: number;
          freeCount: number;
          flowSubscriberCount: number;
          bizxMigratedSubscriberCount: number;
          kuMigratedSubscriberCount: number;
          newRegistrationCount: number;
          invoiceCount: number;
        };
      setCurrentCoreUsers(res.activeCount);
      setCanceledCoreUsers(res.cancelledCount);
      setTrialUsers(res.trialCount);
      setFreeUsers(res.freeCount);
      setNewRegFlowUsers(res.flowSubscriberCount);
      setMigratedBizXUsers(res.bizxMigratedSubscriberCount);
      setMigratedKeyBusinessUsers(res.kuMigratedSubscriberCount);
      setRegisteredUsers(res.newRegistrationCount);
      setInvoiceCount(res.invoiceCount);
    } catch (e) {
      setErrorActualState(true);
      console.error("getActualState failed", e);
      showErrorToast(
        "Hiba történt",
        "Nem sikerült lekérni az aktuális állapotot."
      );
    } finally {
      setLoadingActualState(false);
    }
  };

  const getActiveUsersThisMonth = async () => {
    setLoadingActiveUsersThisMonth(true);
    setErrorActiveUsersThisMonth(false);

    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const fmt = (d: Date) => d.toISOString().slice(0, 10);

    try {
      const res = await AdminService.getAdminKpiActiveAccountsCount(
        fmt(first),
        fmt(last),
        kpiFilterType
      );

      setActiveUsersThisMonth(res);
    } catch (e) {
      setErrorActiveUsersThisMonth(true);
      console.error("getActiveUsersThisMonth failed", e);
      showErrorToast(
        "Hiba történt",
        "Nem sikerült lekérni az aktív havi fiókokat."
      );
    } finally {
      setLoadingActiveUsersThisMonth(false);
    }
  };

  const getDeletableAccounts = async () => {
    setLoadingDeletable(true);
    setErrorDeletable(false);
    try {
      const res = await AdminService.getAdminKpiDeletableAccountsCount(
        dateFilter.startDate,
        dateFilter.endDate,
        kpiFilterType
      );
      setDeletableAccounts(res);
    } catch (e) {
      setErrorDeletable(true);
      console.error("getAdminKpiDeletableAccountsCount failed", e);
      showErrorToast(
        "Hiba történt",
        "Nem sikerült lekérni a törölhető fiókokat."
      );
    } finally {
      setLoadingDeletable(false);
    }
  };

  const getVoucherCount = async () => {
    setLoadingVoucher(true);
    setErrorVoucher(false);
    try {
      const res = await AdminService.getAdminKpiVoucherCount(
        voucherType,
        dateFilter.startDate,
        dateFilter.endDate,
        kpiFilterType
      );
      setVoucherCount(res);
    } catch (e) {
      setErrorVoucher(true);
      console.error("getAdminKpiVoucherCount failed", e);
      showErrorToast(
        "Hiba történt",
        "Nem sikerült lekérni a bizonylatszámokat."
      );
    } finally {
      setLoadingVoucher(false);
    }
  };

  const getInvoiceKpiCount = async () => {
    setLoadingInvoiceKpi(true);
    setErrorInvoiceKpi(false);
    try {
      const res = await AdminService.getAdminKpiInvoiceCount(
        dateFilter.startDate,
        dateFilter.endDate,
        kpiFilterType
      );
      if (typeof res === "number") {
        setInvoiceKpiCount(res);
      } else {
        setInvoiceKpiCount(res ?? invoiceCount ?? null);
      }
    } catch (e: any) {
      setErrorInvoiceKpi(true);
      if (e && e.message)
        showErrorToast(
          "Hiba történt",
          "Nem sikerült lekérni a számlák számát."
        );
    } finally {
      setLoadingInvoiceKpi(false);
    }
  };

  const getRenewalErrors = async () => {
    setLoadingRenewalErrors(true);
    setErrorRenewalErrors(false);
    try {
      const res = await AdminService.getAdminRenewalErrors();
      setRenewalErrors(res?.items ?? res ?? []);
      setRenewalCurrentPage(1);
    } catch (e) {
      setErrorRenewalErrors(true);
      console.error("getRenewalErrors failed", e);
      showErrorToast(
        "Hiba történt",
        "Nem sikerült lekérni a megújítási hibákat."
      );
    } finally {
      setLoadingRenewalErrors(false);
    }
  };

  return (
    <div className={styles.main}>
      <div
        style={{
          textAlign: "center",
          width: "100%",
          fontWeight: "bold",
          fontSize: "40px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        KPI
      </div>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: "20px",
          }}
        >
          <div
            style={{
              alignItems: "center",
              justifyContent: "left",
              display: "flex",
              gap: "6px",
            }}
          >
            <Button onClick={navigateBack}>Vissza</Button>
          </div>

          <div style={{ display: "flex", gap: "20px" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: "14px",
              }}
            >
              <label>Kezdeti dátum:</label>
              <input
                type="date"
                value={dateFilter.startDate}
                onChange={(e) =>
                  setDateFilter({ ...dateFilter, startDate: e.target.value })
                }
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "6px",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: "14px",
              }}
            >
              <label>Záró dátum:</label>
              <input
                type="date"
                value={dateFilter.endDate}
                onChange={(e) =>
                  setDateFilter({ ...dateFilter, endDate: e.target.value })
                }
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "6px",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: "14px",
              }}
            >
              <label>Szűrő típus:</label>
              <Dropdown
                selectedOptions={[kpiFilterType]}
                onOptionSelect={(_, data) =>
                  setKpiFilterType(data.optionValue as KPIFilterType)
                }
                style={{ minWidth: "150px" }}
                placeholder="Mindkettő"
              >
                <Option value={KPIFilterType.BOTH}>Mindkettő</Option>
                <Option value={KPIFilterType.CORE}>Core</Option>
                <Option value={KPIFilterType.FREE}>Free</Option>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "40px",
          width: "100%",
          marginTop: "24px",
        }}
      >
        <Card
          style={{
            flex: 1,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "#F0F4F7",
            borderLeft: "6px solid #23395D",
          }}
        >
          <CardHeader
            header={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <PictureInPicture24Regular />
                <h3>Adott időszakban beregiszrált cégek</h3>
              </div>
            }
            description={<Caption1></Caption1>}
          />

          <footer
            style={{
              gap: "4px",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              paddingBottom: 12,
              paddingRight: 12,
            }}
          >
            <span
              style={{
                fontWeight: 800,
                fontSize: "32px",
                lineHeight: 1,
              }}
            >
              {loadingAllAccounts ? (
                <Spinner size="tiny" />
              ) : errorAllAccounts ? (
                <ErrorCircle24Filled color="red" />
              ) : (
                `${numberFormatter.format(allAccounts) ?? "-"} db`
              )}
            </span>
          </footer>
        </Card>

        <Card
          style={{
            flex: 1,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "#F0F4F7",
            borderLeft: "6px solid #127DBB",
          }}
        >
          <CardHeader
            header={
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <People24Regular />
                  <h3>Aktív ügyfelek száma</h3>
                </div>
                <div>
                  <h4 style={{ margin: 0 }}>
                    A kiválasztott időszakban állított ki bizonylatot
                  </h4>
                </div>
              </div>
            }
          />
          <div style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "14px" }}>Időszak:</label>
            <Dropdown
              selectedOptions={[activeRangeType]}
              onOptionSelect={(_, data) => {
                const newValue = data.optionValue as ActiveRangeType;
                setActiveRangeType(newValue);
              }}
              style={{ width: "100%" }}
              placeholder={getActiveRangeTypeLabel(
                ActiveRangeType.LAST_30_DAYS
              )}
            >
              <Option value={ActiveRangeType.LAST_30_DAYS}>Előző 30 nap</Option>
              <Option value={ActiveRangeType.LAST_CALENDAR_MONTH}>
                Előző naptári hónap
              </Option>
              <Option value={ActiveRangeType.DATE_PICKER}>Dátumválasztó</Option>
            </Dropdown>
          </div>
          <footer
            style={{
              gap: "4px",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              paddingBottom: 12,
              paddingRight: 12,
            }}
          >
            <span
              style={{
                fontWeight: 800,
                fontSize: "32px",
                lineHeight: 1,
              }}
            >
              {loadingActiveAccounts ? (
                <Spinner size="tiny" />
              ) : errorActiveAccounts ? (
                <ErrorCircle24Filled color="red" />
              ) : (
                `${numberFormatter.format(activeAccounts) ?? "-"} db`
              )}
            </span>
          </footer>
        </Card>

        <Card
          style={{
            flex: 1,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "#FFF7E6",
            borderLeft: "6px solid #b39958",
          }}
        >
          <CardHeader
            header={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <PeopleCommunity24Regular />
                <h3>Adott hónapban aktív felhasználók száma</h3>
              </div>
            }
          />

          <footer
            style={{
              gap: "4px",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              paddingBottom: 12,
              paddingRight: 12,
            }}
          >
            <span
              style={{
                fontWeight: 800,
                fontSize: "32px",
                lineHeight: 1,
              }}
            >
              {loadingActiveUsersThisMonth ? (
                <Spinner size="tiny" />
              ) : errorActiveUsersThisMonth ? (
                <ErrorCircle24Filled color="red" />
              ) : (
                `${numberFormatter.format(activeUsersThisMonth) ?? "-"} db`
              )}
            </span>
          </footer>
        </Card>

        <Card
          style={{
            flex: 1,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "#FFEDED",
            borderLeft: "6px solid #FF0000",
          }}
        >
          <CardHeader
            header={
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Delete24Regular />
                  <h3>Törölhető fiókok száma</h3>
                </div>
                <div>
                  <h4 style={{ margin: 0 }}>
                    Az elmúlt 6 hónapban nem rendelkezik aktív előfizetéssel
                  </h4>
                </div>
              </div>
            }
          />

          <footer
            style={{
              gap: "4px",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              paddingBottom: 12,
              paddingRight: 12,
            }}
          >
            <span
              style={{
                fontWeight: 800,
                fontSize: "32px",
                lineHeight: 1,
              }}
            >
              {loadingDeletable ? (
                <Spinner size="tiny" />
              ) : errorDeletable ? (
                <ErrorCircle24Filled color="red" />
              ) : (
                `${numberFormatter.format(deletableAccounts) ?? "-"} db`
              )}
            </span>
          </footer>
        </Card>

        <Card
          style={{
            flex: 1,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "#a4fafaff",
            borderLeft: "6px solid #27bdbe",
          }}
        >
          <CardHeader
            header={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <ReceiptMoney24Regular />
                <h3>Kiállított bizonylatok száma</h3>
              </div>
            }
          />

          <div style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "14px" }}>Bizonylat típus:</label>
            <Dropdown
              selectedOptions={[voucherType]}
              onOptionSelect={(_, data) =>
                setVoucherType(data.optionValue as VoucherType)
              }
              style={{ width: "100%" }}
              placeholder="Számla"
            >
              <Option value={VoucherType.CUSTOMER_INVOICE}>Számla</Option>
              <Option value={VoucherType.CUSTOMER_DELIVERY_BILL}>
                Szállítólevél
              </Option>
              <Option value={VoucherType.CUSTOMER_ORDER}>Rendelés</Option>
              <Option value={VoucherType.CUSTOMER_OFFER}>Ajánlat</Option>
              <Option value={VoucherType.WORKSHEET}>Munkalap</Option>
              <Option value={VoucherType.CUSTOMER_RECEIPT}>Nyugta</Option>
              {/* Add any other relevant voucher types */}
            </Dropdown>
          </div>

          <footer
            style={{
              paddingBottom: 12,
              paddingRight: 12,
              gap: "4px",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "flex-end",
            }}
          >
            <span
              style={{
                fontWeight: 800,
                fontSize: "32px",
                lineHeight: 1,
              }}
            >
              {loadingVoucher ? (
                <Spinner size="tiny" />
              ) : errorVoucher ? (
                <ErrorCircle24Filled color="red" />
              ) : (
                `${numberFormatter.format(voucherCount) ?? "-"} db`
              )}
            </span>
          </footer>
        </Card>
        <Card
          style={{
            flex: 1,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "#E8F7FF",
            borderLeft: "6px solid #0B69A3",
          }}
        >
          <CardHeader
            header={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <ReceiptMoney24Regular />
                <h3>Számlák száma</h3>
              </div>
            }
          />

          <footer
            style={{
              gap: "4px",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              paddingBottom: 12,
              paddingRight: 12,
            }}
          >
            <span
              style={{
                fontWeight: 800,
                fontSize: "32px",
                lineHeight: 1,
              }}
            >
              {loadingInvoiceKpi ? (
                <Spinner size="tiny" />
              ) : errorInvoiceKpi ? (
                <ErrorCircle24Filled color="red" />
              ) : invoiceKpiCount != null ? (
                `${numberFormatter.format(invoiceKpiCount)} db`
              ) : (
                "- db"
              )}
            </span>
          </footer>
        </Card>
        <Card
          style={{
            flex: 1,
            minWidth: 500,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "#fcd9d0ff",
            borderLeft: "6px solid #ef4e22",
          }}
        >
          <CardHeader
            header={
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PeopleSync24Regular />
                  <h3>Aktuális állapot:</h3>
                </div>

                {loadingActualState ? (
                  <div style={{ paddingLeft: 8, paddingTop: 8 }}>
                    <Spinner size="small" />
                  </div>
                ) : errorActualState ? (
                  <div style={{ paddingLeft: 8, color: "red" }}>
                    <h4 style={{ margin: 0 }}>
                      Nem sikerült betölteni az adatokat
                    </h4>
                  </div>
                ) : (
                  <div style={{ paddingLeft: 8, paddingRight: 8 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>Core előfizető:</h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {currentCoreUsers ?? "-"}
                      </h4>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>Core lemondott:</h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {canceledCoreUsers ?? "-"}
                      </h4>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>Core trial:</h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {trialUsers ?? "-"}
                      </h4>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>Free:</h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {freeUsers ?? "-"}
                      </h4>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                        marginTop: "8px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>Számla:</h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {invoiceCount ?? "-"}
                      </h4>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>
                        Flowban regisztrált és előfizetett:
                      </h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {newRegFlowUsers ?? "-"}
                      </h4>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>
                        Előfizetők migrálásból (bizx):
                      </h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {migratedBizXUsers ?? "-"}
                      </h4>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>
                        Előfizetők migrálásból (kulcsuzlet):
                      </h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {migratedKeyBusinessUsers ?? "-"}
                      </h4>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>Flowban regisztrált:</h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {registeredUsers ?? "-"}
                      </h4>
                    </div>
                  </div>
                )}
              </div>
            }
            description={<Caption1></Caption1>}
          />

          <footer
            style={{
              paddingBottom: 12,
              paddingRight: 12,
              gap: "4px",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "flex-end",
            }}
          >
            <span
              style={{
                fontWeight: 800,
                fontSize: "32px",
                lineHeight: 1,
              }}
            ></span>
          </footer>
        </Card>

        <Card
          style={{
            flex: 1,
            minWidth: 500,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "#a4fafaff",
            borderLeft: "6px solid #23395D",
          }}
        >
          <CardHeader
            header={
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PersonStar24Regular />
                  <h3>EV és KATA:</h3>
                </div>
                {loadingKpi ? (
                  <div style={{ paddingLeft: 8, paddingTop: 8 }}>
                    <Spinner size="small" />
                  </div>
                ) : errorKpi ? (
                  <div style={{ paddingLeft: 8, color: "red" }}>
                    <h4 style={{ margin: 0 }}>
                      Nem sikerült betölteni az adatokat
                    </h4>
                  </div>
                ) : (
                  <div style={{ paddingLeft: 8, paddingRight: 8 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>Egyéni vállalkozók száma:</h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {kpiEvCount ?? "-"}
                      </h4>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>KATA-s vállalkozók száma:</h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {kpiKataCount ?? "-"}
                      </h4>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>
                        Egyéni vállalkozók KATA-ban:{" "}
                      </h4>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          textAlign: "right",
                        }}
                      >
                        {kpiEvKataCount ?? "-"}
                      </h4>
                    </div>
                  </div>
                )}
              </div>
            }
            description={<Caption1></Caption1>}
          />

          <footer
            style={{
              paddingBottom: 12,
              paddingRight: 12,
              gap: "4px",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "flex-end",
            }}
          >
            <span
              style={{
                fontWeight: 800,
                fontSize: "32px",
                lineHeight: 1,
              }}
            ></span>
          </footer>
        </Card>
      </div>

      <div
        style={{
          width: "100%",
          marginTop: "40px",
          backgroundColor: "#F0F4F7",
          borderRadius: "8px",
          padding: "16px",
          boxSizing: "border-box",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Megújítási hibák</h3>
        {loadingRenewalErrors ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spinner size="large" label="Betöltés..." />
          </div>
        ) : errorRenewalErrors ? (
          <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
            Hiba történt az adatok betöltésekor
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Dropdown
                  defaultValue={renewalPageSize}
                  onOptionSelect={(_, data) => {
                    setRenewalPageSize(data.optionValue as string);
                    setRenewalCurrentPage(1);
                  }}
                  style={{ minWidth: 100 }}
                >
                  <Option value="10">10</Option>
                  <Option value="25">25</Option>
                </Dropdown>
                <span style={{ fontSize: 14 }}>találat oldalaként</span>
              </div>

              <Pagination
                currentPage={renewalCurrentPage}
                totalItems={renewalErrors.length}
                pageSize={Number(renewalPageSize)}
                onPageChange={(p) => setRenewalCurrentPage(p)}
              />
            </div>

            <div
              style={{
                width: "100%",
                overflowX: "auto",
                marginRight: "-16px",
                marginBottom: "-16px",
                marginLeft: "-16px",
                paddingRight: "16px",
                paddingBottom: "16px",
                paddingLeft: "16px",
                boxSizing: "border-box",
              }}
            >
              <TableComponent
                type={TABLE.RENEWAL_ERRORS}
                data={renewalErrors}
                isLoading={loadingRenewalErrors}
                pageSize={Number(renewalPageSize)}
                currentPage={renewalCurrentPage}
                onPageChange={(p) => setRenewalCurrentPage(p)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
